import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf")
});

const AttestationTravailPDF = ({ data }) => {
  // Vérifications de sécurité
  if (!data || !data.employee) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.errorText}>Erreur: Données de l'employé manquantes</Text>
        </Page>
      </Document>
    );
  }
  
  // Helper function pour gérer le genre
  const getGenderInfo = (employee) => {
    const genre = employee.genre?.toLowerCase();
    const isFemale = genre === 'femme';
    
    return {
      title: isFemale ? 'Madame' : 'Monsieur',
      titleLower: isFemale ? 'madame' : 'monsieur',
      ending: isFemale ? 'e' : ''
    };
  };
  
  // Formatter le numéro de référence (gère les anciens et nouveaux formats)
  const formatReference = (ref) => {
    if (!ref) return '0001DG/2MPI/2026';
    
    // Si la référence est déjà au nouveau format (4 chiffres + DG/2MPI + année)
    if (ref.match(/^\d{4}DG\/2MPI\/\d{4}$/)) {
      return ref;
    }
    
    // Si c'est une ancienne référence (ATT-, CERT-, DEC-), la retourner telle quelle
    if (ref.match(/^(ATT|CERT|DEC)-\d+$/)) {
      return ref;
    }
    
    // Sinon, retourner la référence par défaut
    return ref;
  };

  const BRAND = getBrandingForEmployee(data.employee);
  const genderInfo = getGenderInfo(data.employee);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.logo}>
            <Image src={BRAND.pdfLogo} style={styles.logoImage} />
          </View>

          <View style={styles.headerRow}>
            <Text style={styles.headerReference}>
              Réf : {formatReference(data.reference)}
            </Text>
            <Text style={styles.headerDate}>
              {BRAND.pdfHeader.city}, le {formatDate(data.created_at)}
            </Text>
          </View>

          <Text style={styles.mainTitle}>
            ATTESTATION DE TRAVAIL
          </Text>

          <View style={styles.formGroup}>
            <Text style={styles.fieldArticle}>
              Nous soussignés, la société {BRAND.companyName}, représentée par Monsieur MOUSSAOUI Bilel, en sa qualité de Président du Conseil d'Administration, sise à l'adresse suivante : Lot N° 39, Cité Aidja - étage N°02, Zone Industrielle Boufarik Nord 09400 Blida, attestons par la présente que :
            </Text>            

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              {genderInfo.title} : <Text style={styles.fieldValue}>{data.employee.first_name || '............................'} {data.employee.last_name || '............................'}</Text>
            </Text>

            <Text style={styles.fieldArticle}>
              Né(e) le : <Text style={styles.fieldValue}>{formatDate(data.employee.date_naissance) || '............................'}</Text> à : <Text style={styles.fieldValue}>{data.employee.lieu_naissance || '............................'}</Text>
            </Text>

            <Text style={styles.fieldArticle}>
              Demeurant à : <Text style={styles.fieldValue}>{data.employee.adresse || '............................'}</Text>
            </Text>

            <Text style={styles.fieldArticle}>
              Numéro de Sécurité Social : <Text style={styles.fieldValue}>{data.employee.numero_securite_sociale || '............................'}</Text>
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20}]}>
              Ci-après dénommé « salarié(e) » ; est employé(e) au sein de notre société
            </Text>

            <Text style={styles.fieldArticle}>
              Depuis le : {formatDate(data.employee.date_embauche) || '............................'} à ce jour.
            </Text>

            <Text style={styles.fieldArticle}>
              En qualité de : <Text style={styles.fieldValue}>{data.employee?.poste || '............................'}</Text>.
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              Cette présente attestation est délivrée à la demande de l'intéressé pour servir et valoir ce que de droit.
            </Text>

            <Text style={{ fontSize: 10, textAlign: 'right', marginTop: 80 , marginBottom: 15 }}>
              Président du Conseil d'Administration
            </Text>

            <Text style={{ fontSize: 10, textAlign: 'right', fontWeight: 'bold' }}>
              MOUSSAOUI Bilel
            </Text> 
          </View>

              
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingLeft: 61,
    paddingRight: 61,
    paddingTop: 40,
    paddingBottom: 40,
    backgroundColor: '#FFFFFF'
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  logo: {
    width: '100%',
    marginBottom: 8,
  },
  logoImage: {
    width: '100%',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
    marginTop: 20,
  },
  headerReference: {
    fontSize: 9,
    textAlign: 'left',
  },
  headerDate: {
    fontSize: 9,
    textAlign: 'right',
  },
  mainTitleArabic: {
    fontFamily: 'Amiri',
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
    color: '#1a237e'
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 50,
    marginTop: 40,
    color: '#1a237e'
  },
  formGroup: {
    flex: 1,
    marginBottom: 15
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    borderBottom: '1pt solid #eee',
    paddingBottom: 5
  },
  field: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold'
  },
  fieldValue: {
    fontWeight: 'normal',
    color: '#333'
  },
  arabicLabel: {
    fontFamily: 'Amiri',
    fontSize: 14,
    color: '#666',
    marginLeft: 20
  },
  fieldArticle: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 10,
    textAlign: 'justify'
  },
  signature: {
    marginTop: 40,
    alignItems: 'flex-end'
  },
  signatureRow: {
    alignItems: 'flex-end',
    textAlign: 'right'
  },
  signatureTitle: {
    fontSize: 12,
    textAlign: 'right',
    marginBottom: 5
  },
  signatureName: {
    fontSize: 12,
    textAlign: 'right',
    fontWeight: 'bold'
  },
  errorText: {
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
    marginTop: 100
  }
});

export default AttestationTravailPDF;
